import {
  GridPaginationModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortItem,
  GridSortModel,
} from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getBenefitsColumns } from './components/benefits-list-columns';
import { useBenefitsStore } from 'src/store/benefits/benefits.store';
import { useMyDataStore } from 'src/store/me/me.store';
import { BenefitBulkUpdateInput, Benefits } from 'src/types/benefits';
import { paths } from 'src/routes/paths';
import {
  restoreBenefitsDrawerFilterDraft,
  restoreBenefitsDrawerFilterDraftFromMappedFilters,
  type BenefitStatusFilterValue,
  type BenefitsDrawerApplyPayload,
  type BenefitsDrawerFilterDraft,
  type BenefitsMappedFilters,
} from './components/benefits-drawer-filters.hook';
import { useTableFiltersPersistContext } from 'src/providers/table-filters-persist.provider';
import useNavigate from 'src/hooks/use-navigate';
import { formatBenefitDate } from 'src/utils/benefit-dates';

export type BenefitRow = {
  id: string;
  benefitId: string;
  benefitType: 'coupon' | 'product';
  isNewBenefit: boolean;
  image: string;
  name: string;
  priority: number | null;
  company: string;
  exhibitionEnv: string;
  pointsValue: number;
  type: string;
  totalQuantity: number;
  availableQuantity: number;
  redeemedQuantity: number;
  withdrawnQuantity: number;
  createdAt: string;
  startDate: string | null;
  endDate: string | null;
  tier?: string;
  visibleForTiers?: string[];
  status: string;
};

export type BenefitSortModel = GridSortItem | null;
type BenefitsListTab = 'benefits' | 'reports' | 'highlights';
export type BenefitBulkEditStatus = 'pending' | 'loading' | 'success' | 'warning' | 'error';
export type BenefitBulkEditPhase = 'editing' | 'submitting' | 'completed';
export type BenefitBulkEditChange = Omit<BenefitBulkUpdateInput, 'name' | 'priority'> & {
  image: string;
  name: string;
  priority: number | null;
  originalName: string;
  originalPriority: number | null;
};

type BenefitStatus = 'active' | 'inactive';

const STATUS_CHANGE_DEBOUNCE_MS = 700;
const BENEFIT_STATUS_FILTER_VALUES: BenefitStatusFilterValue[] = [
  'active',
  'inactive',
  'expired',
  'soldOut',
];

const getStatusFilters = (filters: BenefitsMappedFilters = []): BenefitStatusFilterValue[] => {
  const statusValue = filters.find(
    (filter) => filter.field === 'statuses' || filter.field === 'status'
  )?.value;
  let values: string[] = [];

  if (Array.isArray(statusValue)) {
    values = statusValue;
  } else if (typeof statusValue === 'string') {
    values = [statusValue];
  }

  return Array.from(
    new Set(
      values.filter((value): value is BenefitStatusFilterValue =>
        BENEFIT_STATUS_FILTER_VALUES.includes(value as BenefitStatusFilterValue)
      )
    )
  );
};

const withStatusFilters = (
  filters: BenefitsMappedFilters,
  statuses: BenefitStatusFilterValue[]
): BenefitsMappedFilters => {
  const filtersWithoutStatus = filters.filter(
    (filter) => filter.field !== 'statuses' && filter.field !== 'status'
  );

  return statuses.length
    ? [...filtersWithoutStatus, { field: 'statuses', value: statuses }]
    : filtersWithoutStatus;
};
export const BENEFITS_BULK_EDIT_BATCH_SIZE = 50;
const mapSortModelToRequest = (model: BenefitSortModel): BenefitSortModel => {
  if (!model) {
    return null;
  }

  if (model.field === 'pointsValue') {
    return {
      ...model,
      field: 'price',
    };
  }

  return model;
};

const getBenefitImage = (image: unknown): string => {
  if (typeof image === 'string') {
    return image;
  }

  if (Array.isArray(image)) {
    const firstImage = image[0];

    if (typeof firstImage === 'string') {
      return firstImage;
    }

    if (firstImage && typeof firstImage === 'object') {
      const imageData = firstImage as { urlName?: unknown; url?: unknown };
      return String(imageData.urlName || imageData.url || '');
    }
  }

  if (image && typeof image === 'object') {
    const imageData = image as { urlName?: unknown; url?: unknown };
    return String(imageData.urlName || imageData.url || '');
  }

  return '';
};

export default function useBenefitsList() {
  type RequestParams = {
    searchText: string;
    filters: BenefitsMappedFilters;
    drawerFilters: BenefitsDrawerFilterDraft | null;
    sortModel: BenefitSortModel;
    paginationModel: GridPaginationModel;
  };

  const [
    getBenefitsList,
    exportBenefits,
    changeBenefitStatus,
    createBulkEditSession,
    submitBulkEditBatch,
    getBulkEditProgress,
    getBulkEditFeedback,
    benefitsList,
    isLoadingBenefitsList,
  ] = useBenefitsStore((store) => [
    store.getBenefitsList,
    store.exportData,
    store.changeBenefitStatus,
    store.createBulkEditSession,
    store.submitBulkEditBatch,
    store.getBulkEditProgress,
    store.getBulkEditFeedback,
    store.benefitsList,
    store.isLoadingBenefitsList,
  ]);
  const user = useMyDataStore((state) => state.me);
  const permissions = useMyDataStore((state) => state.me?.cnAdminInfo?.permissions?.benefits);
  const { enqueueSnackbar } = useSnackbar();

  const { updateFilters, getFilters } = useTableFiltersPersistContext();

  const searchParamsFilters = getFilters<RequestParams>([
    paths.dashboard.benefitsDetailsEdit(':id'),
    paths.dashboard.benefitsDetails,
  ]);

  const [searchText, setSearchText] = useState<string>(searchParamsFilters?.searchText || '');
  const TABS = [
    { value: 'benefits' as const, label: 'Listagem', icon: 'lucide:list' },
    { value: 'reports' as const, label: 'Relatórios', icon: 'lucide:file-text' },
    { value: 'highlights' as const, label: 'Vitrines', icon: 'lucide:star' },
  ];
  const [currentTab, setCurrentTab] = useState<BenefitsListTab>('benefits');
  const [newsConfigurationRevision, setNewsConfigurationRevision] = useState(0);
  const handleNewsConfigurationSaved = useCallback(() => {
    setNewsConfigurationRevision((current) => current + 1);
  }, []);
  const restoredStatusFilters = getStatusFilters(searchParamsFilters?.filters);
  const [statusFilters, setStatusFilters] =
    useState<BenefitStatusFilterValue[]>(restoredStatusFilters);
  const [filters, setFilters] = useState<BenefitsMappedFilters>(() =>
    withStatusFilters(searchParamsFilters?.filters || [], restoredStatusFilters)
  );
  const [appliedDraftFilters, setAppliedDraftFilters] = useState<BenefitsDrawerFilterDraft | null>(
    restoreBenefitsDrawerFilterDraft(searchParamsFilters?.drawerFilters) ||
      restoreBenefitsDrawerFilterDraftFromMappedFilters(searchParamsFilters?.filters)
  );
  const [openFiltersDrawer, setOpenFiltersDrawer] = useState(false);
  const pendingParamsRef = useRef<RequestParams | null>(null);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const hasPendingRequestRef = useRef(false);
  const hasSkippedFirstDebounceRef = useRef(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [isAllSelectedMode, setIsAllSelectedMode] = useState(false);
  const [selectedCouponIds, setSelectedCouponIds] = useState<string[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [deselectedCouponIds, setDeselectedCouponIds] = useState<string[]>([]);
  const [deselectedProductIds, setDeselectedProductIds] = useState<string[]>([]);
  const [isBulkEditMode, setIsBulkEditMode] = useState(false);
  const [bulkEditChanges, setBulkEditChanges] = useState<Record<string, BenefitBulkEditChange>>({});
  const [committedBulkEdits, setCommittedBulkEdits] = useState<
    Record<string, Pick<BenefitBulkEditChange, 'name' | 'priority'>>
  >({});
  const [bulkEditStatuses, setBulkEditStatuses] = useState<Record<string, BenefitBulkEditStatus>>(
    {}
  );
  const [bulkEditMessages, setBulkEditMessages] = useState<Record<string, string>>({});
  const [bulkEditPhase, setBulkEditPhase] = useState<BenefitBulkEditPhase>('editing');
  const [isBulkEditPanelCollapsed, setIsBulkEditPanelCollapsed] = useState(false);

  const navigate = useNavigate();
  const todayIsoDate = new Date().toISOString().slice(0, 10);

  const [sortModel, setSortModel] = useState<BenefitSortModel>(
    searchParamsFilters?.sortModel?.field === 'isNewBenefit'
      ? null
      : searchParamsFilters?.sortModel || null
  );
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [statusByBenefitId, setStatusByBenefitId] = useState<Record<string, BenefitStatus>>({});
  const statusChangeTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const statusBeforeChangeRef = useRef<Record<string, string>>({});

  useEffect(() => {
    let isMounted = true;

    const fetchInitialData = async () => {
      await getBenefitsList(searchText, filters, mapSortModelToRequest(sortModel), paginationModel);

      if (isMounted) {
        setIsBootstrapping(false);
      }
    };

    fetchInitialData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(
    () => () => {
      Object.values(statusChangeTimersRef.current).forEach(clearTimeout);
      statusChangeTimersRef.current = {};
    },
    []
  );

  useEffect(() => {
    if (bulkEditPhase !== 'submitting') {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [bulkEditPhase]);

  useEffect(() => {
    // Initial load is requested immediately on mount; skip the first debounced cycle.
    const shouldSkipFirstDebounce = !hasSkippedFirstDebounceRef.current;

    if (shouldSkipFirstDebounce) {
      hasSkippedFirstDebounceRef.current = true;
    }

    let handler: ReturnType<typeof setTimeout> | null = null;
    if (!shouldSkipFirstDebounce) {
      handler = setTimeout(() => {
        const fetchData = async () => {
          const currentParams: RequestParams = {
            searchText,
            filters,
            drawerFilters: appliedDraftFilters,
            sortModel: mapSortModelToRequest(sortModel),
            paginationModel,
          };

          updateFilters(JSON.stringify(currentParams));

          if (isLoadingBenefitsList) {
            hasPendingRequestRef.current = true;
            pendingParamsRef.current = currentParams;
            return;
          }

          pendingParamsRef.current = null;
          await getBenefitsList(
            currentParams.searchText,
            currentParams.filters,
            currentParams.sortModel,
            currentParams.paginationModel
          );
        };
        fetchData();
      }, 500);
    }

    return () => {
      if (handler) {
        clearTimeout(handler);
      }
    };
  }, [
    searchText,
    filters,
    appliedDraftFilters,
    sortModel,
    paginationModel,
    newsConfigurationRevision,
  ]);

  useEffect(() => {
    if (hasPendingRequestRef.current && !isLoadingBenefitsList && pendingParamsRef.current) {
      const params = pendingParamsRef.current;
      hasPendingRequestRef.current = false;
      pendingParamsRef.current = null;

      getBenefitsList(params.searchText, params.filters, params.sortModel, params.paginationModel);
    }
  }, [isLoadingBenefitsList, getBenefitsList]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchText, filters, sortModel]);

  useEffect(() => {
    if (!benefitsList) {
      return;
    }

    setPaginationModel((prev) => {
      if (prev.page === benefitsList.page && prev.pageSize === benefitsList.pageSize) {
        return prev;
      }

      return {
        page: benefitsList.page,
        pageSize: benefitsList.pageSize,
      };
    });
  }, [benefitsList]);

  function formatDate(isoDate: string) {
    return formatBenefitDate(isoDate);
  }

  function formatPoints(points: number) {
    return `${points.toLocaleString('pt-BR')} pts`;
  }

  const sourceBenefitsRows = useMemo<BenefitRow[]>(
    () =>
      (benefitsList?.data || []).map((item: Benefits & { id?: string; _id?: string }, index) => {
        const benefitId = String(item.id || item._id || (item as any)?.benefitId || '');
        const exhibitionEnv = String(item.exhibitionEnv || '')
          .trim()
          .toLowerCase();
        const isParking = exhibitionEnv === 'parking' || exhibitionEnv === 'estacionamento';

        return {
          benefitId,
          benefitType: item.benefitType?.toLowerCase() === 'coupon' ? 'coupon' : 'product',
          isNewBenefit: item.isNewBenefit === true,
          image: getBenefitImage((item as { image?: unknown }).image),
          id:
            item.id ||
            item._id ||
            `${benefitsList?.page ?? 0}-${index}-${item.name}-${String(item.startDate)}`,
          name: item.name,
          priority:
            typeof item.priority === 'number' && Number.isFinite(item.priority)
              ? item.priority
              : null,
          company: isParking ? 'Sem empresa associada' : item.entityName,
          exhibitionEnv: item.exhibitionEnv,
          isParking,
          pointsValue: item.price,
          type: item.benefitType?.toLowerCase() === 'coupon' ? 'Cupom' : 'Produto',
          totalQuantity: item.totalQuantity,
          availableQuantity: item.availableQuantity,
          redeemedQuantity: item.redeemedQuantity,
          withdrawnQuantity: item.usedQuantity,
          createdAt: item.startDate ? String(item.startDate) : '',
          startDate: item.startDate ? String(item.startDate) : null,
          endDate: item.endDate ? String(item.endDate) : null,
          tier: item.tier,
          visibleForTiers: item.visibleForTiers,
          status: statusByBenefitId[benefitId] || item.status,
        };
      }),
    [benefitsList, statusByBenefitId]
  );

  const benefitsRows = useMemo(
    () =>
      sourceBenefitsRows.map((row) => {
        const persistedEdit = committedBulkEdits[row.benefitId];
        const pendingEdit = bulkEditChanges[row.benefitId];
        const appliedEdit = pendingEdit || persistedEdit;

        if (!appliedEdit) {
          return row;
        }

        return {
          ...row,
          name: appliedEdit.name,
          priority: appliedEdit.priority,
        };
      }),
    [sourceBenefitsRows, committedBulkEdits, bulkEditChanges]
  );
  const benefitsRowsRef = useRef(benefitsRows);
  benefitsRowsRef.current = benefitsRows;

  const tierColumnWidth = useMemo(() => {
    const maxTierCount = sourceBenefitsRows.reduce((maxCount, row) => {
      const rowTiers = Array.isArray(row.visibleForTiers) ? row.visibleForTiers : [];

      if (rowTiers.length > 0) {
        if (rowTiers.includes('all')) {
          return Math.max(maxCount, 1);
        }

        return Math.max(maxCount, rowTiers.length);
      }

      return Math.max(maxCount, 1);
    }, 1);

    if (maxTierCount >= 3) {
      return 260;
    }

    if (maxTierCount === 2) {
      return 190;
    }

    return 130;
  }, [sourceBenefitsRows]);

  const bulkEditItems = useMemo(() => Object.values(bulkEditChanges), [bulkEditChanges]);
  const invalidBulkEditIds = useMemo(
    () =>
      bulkEditItems
        .filter(
          (item) =>
            !item.name.trim() ||
            [...item.name.trim()].length > 100 ||
            (item.priority !== null &&
              (!Number.isSafeInteger(item.priority) ||
                item.priority < 0 ||
                item.priority > 2147483647)) ||
            (item.priority === null && item.originalPriority !== null)
        )
        .map((item) => item.id),
    [bulkEditItems]
  );
  const isBulkEditLocked = bulkEditPhase !== 'editing';

  const handleBulkEditFieldChange = useCallback(
    (
      row: {
        benefitId: string;
        benefitType: 'coupon' | 'product';
        image: string;
        name: string;
        priority: number | null;
      },
      field: 'name' | 'priority',
      value: string | number | null
    ) => {
      if (!isBulkEditMode || isBulkEditLocked) {
        return;
      }

      const benefitId = String(row.benefitId || '');
      if (!benefitId) {
        return;
      }

      setBulkEditChanges((current) => {
        const currentChange = current[benefitId];
        const originalName = currentChange?.originalName ?? row.name;
        const originalPriority = currentChange?.originalPriority ?? row.priority;
        const name = field === 'name' ? String(value ?? '') : (currentChange?.name ?? row.name);
        let priority: number | null = currentChange?.priority ?? row.priority;

        if (field === 'priority') {
          priority = typeof value === 'number' ? value : null;
        }
        const nextChanges = { ...current };

        if (name === originalName && priority === originalPriority) {
          delete nextChanges[benefitId];
          return nextChanges;
        }

        nextChanges[benefitId] = {
          id: benefitId,
          benefitType: row.benefitType,
          image: currentChange?.image ?? row.image,
          name,
          priority,
          originalName,
          originalPriority,
        };

        return nextChanges;
      });
    },
    [isBulkEditLocked, isBulkEditMode]
  );

  const clearBenefitSelection = useCallback(() => {
    setSelectedRows([]);
    setIsAllSelectedMode(false);
    setSelectedCouponIds([]);
    setSelectedProductIds([]);
    setDeselectedCouponIds([]);
    setDeselectedProductIds([]);
  }, []);

  const handleCancelBulkEdit = useCallback(() => {
    if (bulkEditPhase === 'submitting') {
      return;
    }

    setBulkEditChanges({});
    setBulkEditStatuses({});
    setBulkEditMessages({});
    setBulkEditPhase('editing');
    setIsBulkEditPanelCollapsed(false);
    setIsBulkEditMode(false);
  }, [bulkEditPhase]);

  const handleToggleBulkEditMode = useCallback(() => {
    if (!permissions?.update || bulkEditPhase === 'submitting') {
      return;
    }

    if (isBulkEditMode) {
      handleCancelBulkEdit();
      return;
    }

    clearBenefitSelection();
    setBulkEditPhase('editing');
    setBulkEditStatuses({});
    setBulkEditMessages({});
    setIsBulkEditPanelCollapsed(false);
    setIsBulkEditMode(true);
  }, [
    bulkEditPhase,
    clearBenefitSelection,
    handleCancelBulkEdit,
    isBulkEditMode,
    permissions?.update,
  ]);

  const handleRemoveBulkEditItem = useCallback(
    (benefitId: string) => {
      if (bulkEditPhase !== 'editing') {
        return;
      }

      setBulkEditChanges((current) => {
        const nextChanges = { ...current };
        delete nextChanges[benefitId];
        return nextChanges;
      });
    },
    [bulkEditPhase]
  );

  const handleStartBulkEdit = useCallback(async () => {
    if (
      bulkEditPhase !== 'editing' ||
      bulkEditItems.length === 0 ||
      invalidBulkEditIds.length > 0
    ) {
      return;
    }

    const itemsToUpdate = bulkEditItems.map((item) => ({
      id: item.id,
      benefitType: item.benefitType,
      ...(item.name.trim() !== item.originalName ? { name: item.name.trim() } : {}),
      ...(item.priority !== item.originalPriority && item.priority !== null
        ? { priority: item.priority }
        : {}),
    }));

    setBulkEditStatuses(
      Object.fromEntries(itemsToUpdate.map((item) => [item.id, 'pending'])) as Record<
        string,
        BenefitBulkEditStatus
      >
    );
    setBulkEditPhase('submitting');
    setIsBulkEditPanelCollapsed(false);

    let hasErrors = false;
    let hasWarnings = false;

    try {
      const session = await createBulkEditSession(crypto.randomUUID(), itemsToUpdate.length);

      for (
        let batchStart = 0;
        batchStart < itemsToUpdate.length;
        batchStart += BENEFITS_BULK_EDIT_BATCH_SIZE
      ) {
        const batch = itemsToUpdate.slice(batchStart, batchStart + BENEFITS_BULK_EDIT_BATCH_SIZE);
        const batchIds = batch.map((item) => item.id);

        setBulkEditStatuses((current) => ({
          ...current,
          ...Object.fromEntries(batchIds.map((id) => [id, 'loading'])),
        }));

        // O backend exige slots de 50 sequenciais; cada batch ganha uma chave idempotente própria.
        // eslint-disable-next-line no-await-in-loop
        await submitBulkEditBatch(
          session.bulkSessionKey,
          crypto.randomUUID(),
          batchStart / BENEFITS_BULK_EDIT_BATCH_SIZE,
          batch
        );
      }

      let cursor: string | undefined;
      let progressStatus = 'processing';
      while (
        ![
          'completed',
          'completed_with_warnings',
          'completed_with_errors',
          'submission_failed',
        ].includes(progressStatus)
      ) {
        // eslint-disable-next-line no-await-in-loop
        const progress = await getBulkEditProgress(session.bulkSessionKey, cursor);
        cursor = progress.nextCursor;
        progressStatus = progress.status;

        const updates = progress.batches.flatMap((batch) => batch.items);
        if (updates.length > 0) {
          hasWarnings =
            hasWarnings || updates.some((item) => item.status === 'success_with_warning');
          hasErrors =
            hasErrors || updates.some((item) => ['failed', 'unknown'].includes(item.status));

          setBulkEditStatuses((current) => {
            const nextStatuses = { ...current };
            updates.forEach((item) => {
              if (item.status === 'success') nextStatuses[item._id] = 'success';
              if (item.status === 'success_with_warning') {
                nextStatuses[item._id] = 'warning';
              }
              if (['failed', 'unknown'].includes(item.status)) {
                nextStatuses[item._id] = 'error';
              }
            });
            return nextStatuses;
          });

          setCommittedBulkEdits((current) => {
            const nextEdits = { ...current };
            updates
              .filter((item) => ['success', 'success_with_warning'].includes(item.status))
              .forEach((item) => {
                const change = bulkEditChanges[item._id];
                if (change) nextEdits[item._id] = { name: change.name, priority: change.priority };
              });
            return nextEdits;
          });
        }

        if (
          ![
            'completed',
            'completed_with_warnings',
            'completed_with_errors',
            'submission_failed',
          ].includes(progressStatus)
        ) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      if (progressStatus === 'submission_failed') {
        hasErrors = true;
        setBulkEditStatuses(
          (current) =>
            Object.fromEntries(
              Object.entries(current).map(([id, status]) => [
                id,
                ['success', 'warning'].includes(status) ? status : 'error',
              ])
            ) as Record<string, BenefitBulkEditStatus>
        );
      }

      const feedback = await getBulkEditFeedback(session.bulkSessionKey);
      setBulkEditMessages(
        Object.fromEntries(
          feedback.batches
            .flatMap((batch) => batch.items)
            .map((item) => [item._id, item.error?.message || item.warnings?.[0]?.message || ''])
        )
      );
    } catch {
      hasErrors = true;
      setBulkEditStatuses(
        (current) =>
          Object.fromEntries(
            Object.keys(current).map((id) => [id, current[id] === 'success' ? 'success' : 'error'])
          ) as Record<string, BenefitBulkEditStatus>
      );
    }

    setBulkEditPhase('completed');
    let completionMessage = 'Alterações concluídas com sucesso!';
    if (hasErrors) completionMessage = 'Algumas alterações não puderam ser concluídas.';
    else if (hasWarnings) completionMessage = 'Alterações concluídas com avisos.';

    enqueueSnackbar(completionMessage, {
      variant: hasErrors || hasWarnings ? 'warning' : 'success',
    });
  }, [
    bulkEditItems,
    bulkEditPhase,
    enqueueSnackbar,
    invalidBulkEditIds.length,
    createBulkEditSession,
    getBulkEditFeedback,
    getBulkEditProgress,
    submitBulkEditBatch,
  ]);

  const handleFinishBulkEdit = useCallback(() => {
    if (bulkEditPhase === 'submitting') {
      return;
    }

    setBulkEditChanges({});
    setBulkEditStatuses({});
    setBulkEditMessages({});
    setBulkEditPhase('editing');
    setIsBulkEditPanelCollapsed(false);
    setIsBulkEditMode(false);
  }, [bulkEditPhase]);

  const handleToggleBulkEditPanel = useCallback(() => {
    setIsBulkEditPanelCollapsed((current) => !current);
  }, []);

  const handleChangeStatus = useCallback(
    (benefitId: string, rawStatus: BenefitStatus) => {
      const previousStatus = benefitsRowsRef.current.find(
        (row) => String(row.benefitId) === benefitId
      )?.status;

      if (!Object.prototype.hasOwnProperty.call(statusBeforeChangeRef.current, benefitId)) {
        statusBeforeChangeRef.current[benefitId] = previousStatus || '';
      }

      const statusBeforeChange = statusBeforeChangeRef.current[benefitId];

      setStatusByBenefitId((current) => {
        const next = { ...current };

        if (rawStatus === statusBeforeChange) {
          delete next[benefitId];
          return next;
        }

        next[benefitId] = rawStatus;
        return next;
      });

      if (statusChangeTimersRef.current[benefitId]) {
        clearTimeout(statusChangeTimersRef.current[benefitId]);
      }

      if (rawStatus === statusBeforeChange) {
        delete statusBeforeChangeRef.current[benefitId];
        delete statusChangeTimersRef.current[benefitId];
        return;
      }

      statusChangeTimersRef.current[benefitId] = setTimeout(async () => {
        const rollbackStatus = statusBeforeChangeRef.current[benefitId];

        delete statusChangeTimersRef.current[benefitId];

        try {
          await changeBenefitStatus(benefitId, rawStatus);
          delete statusBeforeChangeRef.current[benefitId];
          enqueueSnackbar('Status do benefício atualizado com sucesso!', { variant: 'success' });
        } catch (error) {
          setStatusByBenefitId((current) => {
            const next = { ...current };

            if (rollbackStatus === 'active' || rollbackStatus === 'inactive') {
              next[benefitId] = rollbackStatus;
              return next;
            }

            delete next[benefitId];
            return next;
          });

          delete statusBeforeChangeRef.current[benefitId];
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      }, STATUS_CHANGE_DEBOUNCE_MS);
    },
    [changeBenefitStatus, enqueueSnackbar]
  );

  const benefitsColumns = useMemo(
    () =>
      getBenefitsColumns({
        formatDate,
        formatPoints,
        handleChangeStatus,
        tierColumnWidth,
        isBulkEditMode,
        isBulkEditLocked,
        onBulkEditFieldChange: handleBulkEditFieldChange,
      }),
    [
      formatDate,
      formatPoints,
      handleBulkEditFieldChange,
      handleChangeStatus,
      isBulkEditLocked,
      isBulkEditMode,
      tierColumnWidth,
    ]
  );

  const isBenefitsTableLoading = isBootstrapping || isLoadingBenefitsList;

  const selectedFiltersCount = useMemo(() => {
    if (!appliedDraftFilters) {
      return 0;
    }

    let total = 0;

    if (appliedDraftFilters.benefitType.length > 0) {
      total += 1;
    }

    if (typeof appliedDraftFilters.isNewBenefit === 'boolean') {
      total += 1;
    }

    if (appliedDraftFilters.tierSort.length > 0) {
      total += 1;
    }

    if (appliedDraftFilters.company.trim()) {
      total += 1;
    }

    if (appliedDraftFilters.startDate || appliedDraftFilters.endDate) {
      total += 1;
    }

    if (appliedDraftFilters.categoriesId.length > 0) {
      total += 1;
    }

    if (appliedDraftFilters.businessUnitId) total += 1;
    if (appliedDraftFilters.lpEventId) total += 1;

    if (appliedDraftFilters.themesId.length > 0) {
      total += 1;
    }

    return total;
  }, [appliedDraftFilters]);

  const gridSortModel = useMemo<GridSortModel>(() => (sortModel ? [sortModel] : []), [sortModel]);

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model.find((item) => item.field !== 'isNewBenefit') ?? null);
  };

  const handleChangeTab = useCallback(
    (newValue: BenefitsListTab) => {
      if (isBulkEditMode) {
        return;
      }

      setCurrentTab(newValue);
    },
    [isBulkEditMode]
  );

  const handleFilterButtonClick = useCallback(() => {
    setOpenFiltersDrawer(true);
  }, []);

  const onCloseFilters = useCallback(() => {
    setOpenFiltersDrawer(false);
  }, []);

  const onApplyFilters = useCallback(
    (payload: BenefitsDrawerApplyPayload) => {
      setFilters(withStatusFilters(payload.mappedFilters, statusFilters));
      setAppliedDraftFilters(payload.draftFilters);
    },
    [statusFilters]
  );

  const handleStatusFiltersChange = useCallback((statuses: BenefitStatusFilterValue[]) => {
    setStatusFilters(statuses);
    setFilters((currentFilters) => withStatusFilters(currentFilters, statuses));
  }, []);

  const handleRowClick = (params: GridRowParams<BenefitRow>) => {
    if (isBulkEditMode) {
      return;
    }

    const benefitId = String(params.row?.benefitId || params.id || '');
    const benefitType =
      params.row?.benefitType ||
      (String(params.row?.type || '')
        .toLowerCase()
        .includes('cupom')
        ? 'coupon'
        : 'product');

    if (!benefitId) {
      return;
    }

    navigate(`${paths.dashboard.benefitsDetailsEdit(benefitId)}?benefitType=${benefitType}`);
  };

  const handleCopySelectedBenefit = useCallback(() => {
    if (!permissions?.create) {
      return;
    }

    if (selectedRows.length !== 1) {
      return;
    }

    const selectedRowId = String(selectedRows[0]);
    const selectedRow = benefitsRows.find((row) => String(row.id) === selectedRowId);

    if (!selectedRow?.benefitId) {
      return;
    }

    navigate(
      `${paths.dashboard.benefitsDetails}?copyId=${selectedRow.benefitId}&benefitType=${selectedRow.benefitType}`
    );
  }, [permissions?.create, selectedRows, benefitsRows, navigate]);

  const onExport = useCallback(async () => {
    const hasSelectedBenefits =
      isAllSelectedMode || selectedCouponIds.length > 0 || selectedProductIds.length > 0;

    if (!hasSelectedBenefits) {
      enqueueSnackbar('Por favor, selecione ao menos um benefício.', { variant: 'error' });
      return;
    }

    if (isAllSelectedMode) {
      const deselectedBenefitsToExport = [
        ...deselectedCouponIds.map((id) => ({ id, type: 'coupon' as const })),
        ...deselectedProductIds.map((id) => ({ id, type: 'product' as const })),
      ];

      const userName = user?.name || 'Desconhecido';
      setIsLoadingExport(true);
      await exportBenefits(deselectedBenefitsToExport, userName, {
        isAllSelected: true,
      });

      enqueueSnackbar('Seu relatório está sendo gerado!', { variant: 'info' });
      setSelectedRows([]);
      setIsAllSelectedMode(false);
      setSelectedCouponIds([]);
      setSelectedProductIds([]);
      setDeselectedCouponIds([]);
      setDeselectedProductIds([]);
      setIsLoadingExport(false);
      return;
    }

    const selectedBenefitsToExport = [
      ...selectedCouponIds.map((id) => ({ id, type: 'coupon' as const })),
      ...selectedProductIds.map((id) => ({ id, type: 'product' as const })),
    ];

    if (selectedBenefitsToExport.length === 0) {
      enqueueSnackbar('Não foi possível identificar os benefícios para exportação.', {
        variant: 'error',
      });
      return;
    }

    const userName = user?.name || 'Desconhecido';
    setIsLoadingExport(true);
    await exportBenefits(selectedBenefitsToExport, userName, {
      isAllSelected: false,
    });

    enqueueSnackbar('Seu relatório está sendo gerado!', { variant: 'info' });
    setSelectedRows([]);
    setIsAllSelectedMode(false);
    setSelectedCouponIds([]);
    setSelectedProductIds([]);
    setDeselectedCouponIds([]);
    setDeselectedProductIds([]);
    setIsLoadingExport(false);
  }, [
    isAllSelectedMode,
    selectedCouponIds,
    selectedProductIds,
    deselectedCouponIds,
    deselectedProductIds,
    user?.name,
    exportBenefits,
    enqueueSnackbar,
  ]);

  const onRowSelectionModelChange = (model: GridRowSelectionModel) => {
    setSelectedRows(model);

    if (isAllSelectedMode) {
      const selectedIds = new Set(model.map((rowId) => String(rowId)));

      setDeselectedCouponIds((prev) => {
        const next = new Set(prev);

        benefitsRows
          .filter((row) => row.benefitType === 'coupon' && row.benefitId)
          .forEach((row) => {
            const rowId = String(row.id);

            if (selectedIds.has(rowId)) {
              next.delete(row.benefitId);
              return;
            }

            next.add(row.benefitId);
          });

        return Array.from(next);
      });

      setDeselectedProductIds((prev) => {
        const next = new Set(prev);

        benefitsRows
          .filter((row) => row.benefitType === 'product' && row.benefitId)
          .forEach((row) => {
            const rowId = String(row.id);

            if (selectedIds.has(rowId)) {
              next.delete(row.benefitId);
              return;
            }

            next.add(row.benefitId);
          });

        return Array.from(next);
      });
    } else {
      // Rastrear seleções individuais entre páginas
      const selectedIds = new Set(model.map((rowId) => String(rowId)));

      setSelectedCouponIds((prev) => {
        const next = new Set(prev);

        benefitsRows
          .filter((row) => row.benefitType === 'coupon' && row.benefitId)
          .forEach((row) => {
            const rowId = String(row.id);

            if (selectedIds.has(rowId)) {
              next.add(row.benefitId);
              return;
            }

            next.delete(row.benefitId);
          });

        return Array.from(next);
      });

      setSelectedProductIds((prev) => {
        const next = new Set(prev);

        benefitsRows
          .filter((row) => row.benefitType === 'product' && row.benefitId)
          .forEach((row) => {
            const rowId = String(row.id);

            if (selectedIds.has(rowId)) {
              next.add(row.benefitId);
              return;
            }

            next.delete(row.benefitId);
          });

        return Array.from(next);
      });
    }
  };

  const onHeaderSelectionCheckboxChange = useCallback(
    (checked: boolean) => {
      setIsAllSelectedMode(checked);

      if (checked) {
        setSelectedCouponIds([]);
        setSelectedProductIds([]);
        setDeselectedCouponIds([]);
        setDeselectedProductIds([]);
        setSelectedRows(benefitsRows.map((row) => String(row.id)));
        return;
      }

      setSelectedRows([]);
      setSelectedCouponIds([]);
      setSelectedProductIds([]);
      setDeselectedCouponIds([]);
      setDeselectedProductIds([]);
    },
    [benefitsRows]
  );

  useEffect(() => {
    if (isBulkEditMode || !isAllSelectedMode) {
      return;
    }

    const deselectedCouponIdsSet = new Set(deselectedCouponIds);
    const deselectedProductIdsSet = new Set(deselectedProductIds);

    const nextSelectedRows = benefitsRows
      .filter((row) => {
        if (!row.benefitId) {
          return false;
        }

        if (row.benefitType === 'coupon') {
          return !deselectedCouponIdsSet.has(row.benefitId);
        }

        return !deselectedProductIdsSet.has(row.benefitId);
      })
      .map((row) => String(row.id));

    setSelectedRows(nextSelectedRows);
  }, [isAllSelectedMode, benefitsRows, deselectedCouponIds, deselectedProductIds, isBulkEditMode]);

  useEffect(() => {
    if (isBulkEditMode || isAllSelectedMode) {
      return;
    }

    // Sincronizar selectedRows com as seleções individuais rastreadas
    const selectedCouponIdsSet = new Set(selectedCouponIds);
    const selectedProductIdsSet = new Set(selectedProductIds);

    const nextSelectedRows = benefitsRows
      .filter((row) => {
        if (!row.benefitId) {
          return false;
        }

        if (row.benefitType === 'coupon') {
          return selectedCouponIdsSet.has(row.benefitId);
        }

        return selectedProductIdsSet.has(row.benefitId);
      })
      .map((row) => String(row.id));

    setSelectedRows(nextSelectedRows);
  }, [isAllSelectedMode, benefitsRows, selectedCouponIds, selectedProductIds, isBulkEditMode]);

  const hasAnySelection = useMemo(
    () => isAllSelectedMode || selectedCouponIds.length > 0 || selectedProductIds.length > 0,
    [isAllSelectedMode, selectedCouponIds, selectedProductIds]
  );

  const totalSelectionCount = useMemo(() => {
    if (isAllSelectedMode) {
      return Infinity; // Indica "select all", desativa botão de copiar
    }
    return selectedCouponIds.length + selectedProductIds.length;
  }, [isAllSelectedMode, selectedCouponIds, selectedProductIds]);

  return {
    benefitsList,
    benefitsRows,
    isLoadingBenefitsList,
    isBenefitsTableLoading,
    TABS,
    currentTab,
    handleNewsConfigurationSaved,
    searchText,
    sortModel,
    gridSortModel,
    paginationModel,
    todayIsoDate,
    benefitsColumns,
    selectedRows,
    selectedFiltersCount,
    statusFilters,
    appliedDraftFilters,
    setSearchText,
    setFilters,
    setPaginationModel,
    setSortModel,
    handleSortModelChange,
    handleRowClick,
    formatDate,
    formatPoints,
    navigate,
    handleFilterButtonClick,
    onCloseFilters,
    onApplyFilters,
    handleStatusFiltersChange,
    openFiltersDrawer,
    onRowSelectionModelChange,
    onHeaderSelectionCheckboxChange,
    handleChangeTab,
    handleCopySelectedBenefit,
    onExport,
    isLoadingExport,
    hasAnySelection,
    totalSelectionCount,
    canCreateBenefits: Boolean(permissions?.create),
    canUpdateBenefits: Boolean(permissions?.update),
    isBulkEditMode,
    isBulkEditLocked,
    bulkEditItems,
    bulkEditStatuses,
    bulkEditMessages,
    bulkEditPhase,
    invalidBulkEditIds,
    isBulkEditPanelCollapsed,
    handleToggleBulkEditMode,
    handleCancelBulkEdit,
    handleRemoveBulkEditItem,
    handleStartBulkEdit,
    handleFinishBulkEdit,
    handleToggleBulkEditPanel,
  };
}
